import React, { useCallback, useEffect, useRef, useState } from 'react';
import { resolveGameIconUrl } from '../../../utils/gameHelpers';
import './flipCardCarousel.css';

/**
 * Flip cards with horizontal slider on mobile (2 visible) + arrows + auto-advance.
 * Desktop keeps a responsive grid; one card flips at a time in the cycle.
 */
export default function FlipCardCarousel({
  cards = [],
  backImage = '',
  rotateInterval = 5000,
  holdMs = 3500,
  autoMs = 4000,
}) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const trackRef = useRef(null);
  const pauseUntilRef = useRef(0);

  const scrollByPage = useCallback((dir) => {
    const el = trackRef.current;
    if (!el) return;
    const step = el.clientWidth;
    const max = Math.max(0, el.scrollWidth - el.clientWidth);
    if (max <= 4) return;
    let next;
    if (dir > 0 && el.scrollLeft >= max - 4) next = 0;
    else if (dir < 0 && el.scrollLeft <= 4) next = max;
    else next = Math.min(max, Math.max(0, el.scrollLeft + dir * step));
    el.scrollTo({ left: next, behavior: 'smooth' });
  }, []);

  const pauseAuto = useCallback(() => {
    pauseUntilRef.current = Date.now() + autoMs * 2;
  }, [autoMs]);

  const go = useCallback(
    (dir) => {
      pauseAuto();
      scrollByPage(dir);
    },
    [pauseAuto, scrollByPage]
  );

  useEffect(() => {
    if (cards.length <= 2 || !autoMs) return undefined;
    const id = setInterval(() => {
      if (Date.now() < pauseUntilRef.current) return;
      const el = trackRef.current;
      if (!el) return;
      // Only auto-advance when track is in horizontal slider mode (mobile).
      if (el.scrollWidth <= el.clientWidth + 4) return;
      scrollByPage(1);
    }, autoMs);
    return () => clearInterval(id);
  }, [cards.length, autoMs, scrollByPage]);

  useEffect(() => {
    if (cards.length === 0) return undefined;

    setFlipped(false);
    setActiveIndex(0);

    let flipTimer;
    let holdTimer;
    let advanceTimer;
    let cancelled = false;

    const runCycle = (index) => {
      if (cancelled || cards.length === 0) return;

      setActiveIndex(index);
      setFlipped(false);

      flipTimer = setTimeout(() => {
        if (cancelled) return;
        setFlipped(true);

        holdTimer = setTimeout(() => {
          if (cancelled) return;
          setFlipped(false);

          advanceTimer = setTimeout(() => {
            if (cancelled) return;
            const next = (index + 1) % cards.length;
            runCycle(next);
          }, 700);
        }, holdMs);
      }, Math.max(400, rotateInterval - holdMs - 700));
    };

    runCycle(0);

    return () => {
      cancelled = true;
      clearTimeout(flipTimer);
      clearTimeout(holdTimer);
      clearTimeout(advanceTimer);
    };
  }, [cards.length, rotateInterval, holdMs]);

  const iconUrl = (image) => resolveGameIconUrl(image);
  const backUrl = backImage ? resolveGameIconUrl(backImage) : '';

  if (!cards.length) return null;

  return (
    <div className="flip-carousel" role="region" aria-label="Category cards">
      <button type="button" className="flip-carousel-arrow" aria-label="Previous" onClick={() => go(-1)}>
        &#8592;
      </button>

      <div
        className="flip-carousel-track"
        ref={trackRef}
        onPointerDown={pauseAuto}
        onTouchStart={pauseAuto}
      >
        {cards.map((card, i) => {
          const isActiveFlip = i === activeIndex && flipped;
          return (
            <div key={card.key || i} className="flip-box">
              <div
                className="flip-box-inner"
                style={{ transform: isActiveFlip ? 'rotateY(180deg)' : 'rotateY(0deg)' }}
              >
                <div className="flip-box-front">
                  {card.image ? (
                    <img src={iconUrl(card.image)} alt={card.name} />
                  ) : (
                    <span className="flip-box-front-label">{card.name}</span>
                  )}
                </div>
                <div
                  className="flip-box-back"
                  style={
                    backUrl
                      ? {
                          backgroundImage: `url(${backUrl})`,
                          backgroundSize: 'contain',
                          backgroundRepeat: 'no-repeat',
                          backgroundPosition: 'center',
                          backgroundColor: 'transparent',
                        }
                      : undefined
                  }
                >
                  <h4>{card.description}</h4>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <button type="button" className="flip-carousel-arrow" aria-label="Next" onClick={() => go(1)}>
        &#8594;
      </button>
    </div>
  );
}
